import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../store/auth';
import { safeReturnPath } from '../auth/browser-session.mjs';
import { startRouteLoading, stopRouteLoading } from './loading';

const APP_TITLE = '直连数据中转管理台';

const routes = [
  { path: '/', redirect: '/dashboard' },
  {
    path: '/login',
    name: 'LoginBridge',
    component: () => import('../views/LoginBridgeView.vue'),
    meta: { title: '统一登录' },
  },
  {
    path: '/login-complete',
    name: 'LoginComplete',
    component: () => import('../views/LoginBridgeView.vue'),
    props: { completion: true },
    meta: { title: '完成登录' },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { requiresAuth: true, title: '看板' },
  },
  {
    path: '/payloads',
    name: 'Payloads',
    component: () => import('../views/PayloadsView.vue'),
    meta: { requiresAuth: true, title: '原始数据' },
  },
  {
    path: '/push-logs',
    name: 'PushLogs',
    component: () => import('../views/PushLogsView.vue'),
    meta: { requiresAuth: true, title: '推送日志' },
  },
  {
    path: '/api-keys',
    name: 'ApiKeys',
    component: () => import('../views/ApiKeysView.vue'),
    meta: { requiresAuth: true, title: 'API 密钥' },
  },
  {
    path: '/service-tokens',
    name: 'ServiceTokens',
    component: () => import('../views/ServiceTokensView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '服务令牌' },
  },
  {
    path: '/tcp-monitor',
    name: 'TcpMonitor',
    component: () => import('../views/TcpMonitorView.vue'),
    meta: { requiresAuth: true, title: 'TCP 监控' },
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import('../views/UnauthorizedView.vue'),
    meta: { title: '无法访问' },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  startRouteLoading();
  const authStore = useAuthStore();
  if (to.meta.requiresAuth || to.path === '/login') {
    try {
      await authStore.fetchProfile();
    } catch {
      // 未登录或验证失败时由本站登录桥重新获取凭据。
    }
  }

  if (to.path === '/login' && authStore.isAuthenticated) {
    next(safeReturnPath(to.query.return_to || '/dashboard'));
    return;
  }

  if (!to.meta.requiresAuth) {
    next();
    return;
  }

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { return_to: to.fullPath } });
    return;
  }

  if (!authStore.profileVerified) {
    try {
      await authStore.fetchProfile();
    } catch {
      if (!authStore.isAuthenticated) {
        next({ path: '/login', query: { return_to: to.fullPath } });
        return;
      }
    }
  }

  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    next('/unauthorized');
    return;
  }

  next();
});

router.afterEach((to) => {
  stopRouteLoading();
  document.title = to.meta.title ? `${to.meta.title} - ${APP_TITLE}` : APP_TITLE;
});

export default router;
